import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Snackbar,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const Add = ({ onRecordAdded, ptpStatusNotes, ptpStatusNotesLoading }) => {
  const { addData: addFirestoreData } = useFirestore("CtagRetrievalForm"); // Use Firestore hook

  // Get status notes from CtagPermissionStatusNote collection
  const { data: statusNotesData, loading: statusNotesLoading } = useFirestore(
    "CtagPermissionStatusNote",
  );

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const initialFormData = {
    Data_Status_Note: "",
    Last_Name: "",
    First_Name: "",
    DOB: null,
    Tartan_Id: "",
    Sinclair_Email: "",
    HSGY: "",
    Personal_Email: "",
    Ohio_SSID: "",
    Current_Sinclair_Student: "",
    Earned_CTAG: "",
    CTAG_Site_of_Delivery: "",
    Forms_Sent_From_CT_Institute: "",
    CTAG_Instructor: "",
    Phone_Number: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDateChange = (newValue) => {
    setFormData((prevData) => ({
      ...prevData,
      DOB: newValue,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSnackbarMessage("");
    try {
      // Add created_at timestamp
      const dataToAdd = { ...formData, created_at: new Date().toISOString() };
      await addFirestoreData(dataToAdd);
      setSnackbarMessage("Record added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setFormData(initialFormData);
      setOpen(false);
      onRecordAdded(); // Call the callback function to refresh the list
    } catch (error) {
      console.error("Error adding record:", error);
      setSnackbarMessage(`Error adding record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <div>
      <Button
        variant="contained"
        color="success"
        onClick={() => setOpen(true)}
        sx={{ marginBottom: "10px", color: "white" }}
      >
        Add Record
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <ModalDialogTitle onClose={() => setOpen(false)}>
          Add Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="data-status-note-label">
                  PTP Status Note
                </InputLabel>
                <Select
                  labelId="data-status-note-label"
                  name="Data_Status_Note"
                  value={formData.Data_Status_Note}
                  onChange={handleChange}
                  label="PTP Status Note"
                  disabled={ptpStatusNotesLoading}
                >
                  {ptpStatusNotesLoading && (
                    <MenuItem disabled>Loading...</MenuItem>
                  )}
                  {!ptpStatusNotesLoading &&
                    (!ptpStatusNotes || ptpStatusNotes.length === 0) && (
                      <MenuItem disabled>No status notes available</MenuItem>
                    )}
                  {ptpStatusNotes &&
                    ptpStatusNotes
                      .map((statusNote) => statusNote.statusNote)
                      .filter(Boolean)
                      .sort((a, b) => a.localeCompare(b))
                      .map((statusNote) => (
                        <MenuItem key={statusNote} value={statusNote}>
                          {statusNote}
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Name"
                name="Last_Name"
                value={formData.Last_Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="First Name"
                name="First_Name"
                value={formData.First_Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Tartan ID"
                name="Tartan_Id"
                value={formData.Tartan_Id}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Email"
                name="Sinclair_Email"
                value={formData.Sinclair_Email}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="HSGY"
                name="HSGY"
                value={formData.HSGY}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Personal Email"
                name="Personal_Email"
                value={formData.Personal_Email}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Ohio SSID"
                name="Ohio_SSID"
                value={formData.Ohio_SSID}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Current Sinclair Student"
                name="Current_Sinclair_Student"
                value={formData.Current_Sinclair_Student}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Earned CTAG"
                name="Earned_CTAG"
                value={formData.Earned_CTAG}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTAG Site of Delivery"
                name="CTAG_Site_of_Delivery"
                value={formData.CTAG_Site_of_Delivery}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Forms Sent From CT Institute"
                name="Forms_Sent_From_CT_Institute"
                value={formData.Forms_Sent_From_CT_Institute}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTAG Instructor"
                name="CTAG_Instructor"
                value={formData.CTAG_Instructor}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Phone Number"
                name="Phone_Number"
                value={formData.Phone_Number}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date of Birth"
                  value={formData.DOB}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                    },
                  }}
                  maxDate={dayjs()}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Add;
